import React, { useState, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TRANSLATION_LANGUAGES } from "../../constants/translationLanguages";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import PaidBadge from "../PaidBadge";
import {
	getPixelAdDisplayName,
	getPixelAdId,
	getPixelCampaignName,
} from "../../utils/pixelCreative";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading, ticket, onTicketUpdate }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [pixelTracking, setPixelTracking] = useState(null);

	useEffect(() => {
		if (!open || !ticket?.id) {
			setPixelTracking(null);
			return undefined;
		}

		const fetchPixelTracking = async () => {
			try {
				const { data } = await api.get(`/pixel/ticket/${ticket.id}`);
				setPixelTracking(data.pixelTracking || null);
			} catch (err) {
				setPixelTracking(null);
			}
		};

		fetchPixelTracking();
	}, [open, ticket?.id]);

	const creativeName = getPixelAdDisplayName(pixelTracking);
	const adId = getPixelAdId(pixelTracking);
	const campaignName = getPixelCampaignName(pixelTracking);

	const handleCustomerLanguageChange = async e => {
		if (!ticket?.id) return;
		try {
			const { data } = await api.put(`/tickets/${ticket.id}`, {
				customerLanguage: e.target.value,
			});
			if (onTicketUpdate) {
				onTicketUpdate(data);
			}
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography style={{ display: "inline-flex", alignItems: "center" }}>
							{contact.name}
							<PaidBadge isPaid={contact.isPaid} />
						</Typography>
						<Typography>
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
					<Paper square variant="outlined" className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						{ticket?.id && (creativeName || adId || campaignName) && (
							<>
								{creativeName && (
									<>
										<Typography variant="subtitle1">
											{i18n.t("pixel.adLabel")}
										</Typography>
										<Typography variant="body2">{creativeName}</Typography>
									</>
								)}
								{adId && (
									<>
										<Typography variant="subtitle1">
											{i18n.t("pixel.adId")}
										</Typography>
										<Typography variant="body2">{adId}</Typography>
									</>
								)}
								{campaignName && campaignName !== creativeName && (
									<>
										<Typography variant="subtitle1">
											{i18n.t("pixel.campaignLabel")}
										</Typography>
										<Typography variant="body2">{campaignName}</Typography>
									</>
								)}
							</>
						)}
						{ticket?.id && (
							<>
								<Typography variant="subtitle1">
									{i18n.t("contactDrawer.customerLanguage")}
								</Typography>
								<Select
									fullWidth
									native
									variant="outlined"
									margin="dense"
									value={ticket.customerLanguage || ""}
									onChange={handleCustomerLanguageChange}
								>
									<option value="">
										{i18n.t("contactDrawer.customerLanguageAuto")}
									</option>
									{TRANSLATION_LANGUAGES.map((lang) => (
										<option key={lang.code} value={lang.code}>
											{lang.label}
										</option>
									))}
								</Select>
							</>
						)}
						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
